import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Category_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaTrash, FaPlus, FaFilter } from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

// Component for managing categories with add, edit, delete, filter, and multiple delete functionality
export default function Categories() {
  const { userData } = useAuth();
  const [categories, setCategories] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    cat_id: [],
    Name: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const columnDisplay = {
    cat_id: "Cat ID",
    Name: "Name",
  };

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = categories.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "cat_id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.cat_id.length === 0 || filters.cat_id.includes(item.cat_id)) &&
      (filters.Name.length === 0 || filters.Name.includes(item.Name))
    );
  };

  // Filter categories (without searchText from SearchContext)
  const filteredCategories = categories.filter(applyFilters);

  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch all categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${Category_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [userData.access_token]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered categories
  const handleSelectAll = () => {
    if (selectedRows.length === filteredCategories.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredCategories.map((item) => item.cat_id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected categories?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${Category_API}/delete/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                },
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("Server Error:", errorText);
                throw new Error(`Failed to delete category ${id}`);
              }
            })
          );

          const updatedCategories = categories.filter(
            (cat) => !selectedRows.includes(cat.cat_id)
          );
          setCategories(updatedCategories);
          setSelectedRows([]);

          Swal.fire(
            "Success!",
            "Selected categories deleted successfully",
            "success"
          );
        } catch (error) {
          console.error("Error deleting categories:", error);
          Swal.fire(
            "Error",
            "Failed to delete some categories. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Function to add a new category using SweetAlert for input
  const addCategory = async () => {
    const { value: name } = await Swal.fire({
      title: "Create new category",
      input: "text",
      inputLabel: "Category Name",
      inputPlaceholder: "Enter category name",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return "You need to enter a category name!";
        }
      },
    });

    if (name) {
      try {
        const response = await fetch(`${Category_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
          },
          body: JSON.stringify({ Name: name }),
        });

        if (!response.ok) {
          throw new Error("Failed to add category");
        }

        const newCategory = await response.json();
        setCategories([...categories, newCategory]);
        Swal.fire("Success!", "Category added successfully.", "success");
      } catch (error) {
        console.error("Error adding category:", error);
        Swal.fire(
          "Error",
          "Failed to add category. Please try again.",
          "error"
        );
      }
    }
  };

  // Function to edit an existing category using SweetAlert for input
  const editCategory = async (c) => {
    const { value: newName } = await Swal.fire({
      title: "Edit category",
      input: "text",
      inputLabel: "Category Name",
      inputValue: c.Name,
      inputPlaceholder: "Enter new category name",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return "You need to enter a category name!";
        }
      },
    });

    if (newName) {
      try {
        const response = await fetch(`${Category_API}/update/${c.cat_id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
          },
          body: JSON.stringify({ Name: newName }),
        });

        if (!response.ok) {
          throw new Error("Failed to update category");
        }

        const updatedCategory = await response.json();
        setCategories(
          categories.map((cat) =>
            cat.cat_id === c.cat_id ? updatedCategory : cat
          )
        );
        Swal.fire("Success!", "Category updated successfully.", "success");
      } catch (error) {
        console.error("Error updating category:", error);
        Swal.fire(
          "Error",
          "Failed to update category. Please try again.",
          "error"
        );
      }
    }
  };

  // Function to delete a single category with confirmation
  const deleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${Category_API}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete category");
        }

        setCategories(categories.filter((cat) => cat.cat_id !== id));
        Swal.fire("Deleted!", "Category has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting category:", error);
        Swal.fire(
          "Error",
          "Failed to delete category. Please try again.",
          "error"
        );
      }
    }
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div
        className="filter-popup"
        style={{
          position: "absolute",
          top: "100%",
          left: "0",
          zIndex: 100,
        }}
      >
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(tempSelected.filter((v) => v !== val));
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Categories - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>Categories</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredCategories.length &&
                    filteredCategories.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredCategories.length &&
                      filteredCategories.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                Cat ID
                <FaFilter
                  className={`filter-icon ${
                    filters.cat_id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("cat_id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "cat_id" && renderFilterPopup("cat_id")}
              </th>
              <th style={{ position: "relative" }}>
                Name
                <FaFilter
                  className={`filter-icon ${
                    filters.Name.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("Name")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "Name" && renderFilterPopup("Name")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedCategories.map((c, index) => (
              <tr
                key={c.cat_id}
                className={selectedRows.includes(c.cat_id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(c.cat_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(c.cat_id)}
                    onChange={() => toggleRowSelection(c.cat_id)}
                  />
                </td>
                <td>{c.cat_id}</td>
                <td>{c.Name}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => editCategory(c)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteCategory(c.cat_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addCategory}>
        <FaPlus />
      </button>
    </div>
  );
}
